import { ArrowDownTrayIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Input } from '@heroui/input';
import { useDisclosure } from '@heroui/modal';
import type { Selection } from '@heroui/table';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { EmptyState, ErrorState, Icon, LoadingSpinner, PageHeader, TablePagination } from '@/components/common';
import { MassUpdateModal } from '@/components/site-lists';
import { ImportSiteFromCrmModal, SitesTable } from '@/components/sites';
import { useDebounce } from '@/hooks/useDebounce';
import { searchKeys, useSitesPage } from '@/queries/search.queries';

const PAGE_SIZE = 25;

/** Mirror of SiteSelection in SiteListSitesTab — kept local until we extract a shared module. */
type SiteSelection = { mode: 'ids'; selected: Set<string> } | { mode: 'cohort'; excluded: Set<string> };
const emptyIdsSelection: SiteSelection = { mode: 'ids', selected: new Set() };

export const Route = createFileRoute('/sites/')({
  component: Sites,
});

function Sites() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isOpen: isImportOpen, onOpen: onImportOpen, onClose: onImportClose } = useDisclosure();

  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<SiteSelection>(emptyIdsSelection);
  const [isMassUpdateOpen, setIsMassUpdateOpen] = useState(false);

  const { data, isLoading, isFetching, error } = useSitesPage(search.trim(), page, PAGE_SIZE);
  const visibleSites = useMemo(() => data?.data ?? [], [data]);
  const total = data?.totalFound ?? 0;

  // Map the discriminated selection to HeroUI's Selection on the visible page.
  const visibleIds = useMemo(() => visibleSites.map((s) => s.id), [visibleSites]);
  const heroSelectedKeys: Selection = useMemo(() => {
    if (selection.mode === 'cohort') {
      return new Set(visibleIds.filter((id) => !selection.excluded.has(id)));
    }
    return new Set([...selection.selected].filter((id) => visibleIds.includes(id)));
  }, [selection, visibleIds]);

  const handleSelectionChange = (next: Selection) => {
    if (selection.mode === 'cohort') {
      const nextSelected = next === 'all' ? new Set(visibleIds) : (next as Set<string>);
      const newlyExcluded = new Set(selection.excluded);
      for (const id of visibleIds) {
        if (nextSelected.has(id)) newlyExcluded.delete(id);
        else newlyExcluded.add(id);
      }
      setSelection({ mode: 'cohort', excluded: newlyExcluded });
      return;
    }
    if (next === 'all') {
      setSelection({ mode: 'ids', selected: new Set(visibleIds) });
      return;
    }
    setSelection({ mode: 'ids', selected: new Set(next as Set<string>) });
  };

  const allVisibleSelected =
    visibleIds.length > 0 &&
    (selection.mode === 'cohort'
      ? visibleIds.every((id) => !selection.excluded.has(id))
      : visibleIds.every((id) => selection.selected.has(id)));

  const effectiveCount =
    selection.mode === 'cohort' ? Math.max(0, total - selection.excluded.size) : selection.selected.size;

  // Cross-page cohort works on filtered set (backend resolves via Typesense)
  // OR on empty filtered/searched whole-collection. Hide while text search is
  // active (cohort resolve ignores text q for now).
  const showSelectAllBanner =
    selection.mode === 'ids' && allVisibleSelected && total > visibleIds.length && search.trim() === '';

  const clearSelection = () => setSelection(emptyIdsSelection);
  const handleSelectAllInList = () => setSelection({ mode: 'cohort', excluded: new Set() });

  const handleApplied = () => {
    void queryClient.invalidateQueries({ queryKey: searchKeys.all });
    clearSelection();
  };

  return (
    <div className="py-6">
      <div className="flex flex-col gap-4">
        <PageHeader
          title="Sites"
          description="A list of all sites including connection points, addresses, and associated billing information."
          action={{ label: 'Import from Zoho', icon: ArrowDownTrayIcon, onPress: onImportOpen }}
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <ErrorState title="Failed to load sites" message="Please try again later" />
        ) : (
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
              <h2 className="text-lg font-semibold">All Sites</h2>
              <Input
                value={searchInput}
                onValueChange={(v) => {
                  setSearchInput(v);
                  setPage(1);
                }}
                placeholder="Search sites..."
                startContent={<Icon as={MagnifyingGlassIcon} size="md" color="default" />}
                variant="bordered"
                className="w-full sm:max-w-xs"
                classNames={{ input: 'text-sm' }}
              />
            </CardHeader>

            {effectiveCount > 0 && (
              <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 border-y border-primary-100">
                <span className="text-sm font-semibold text-primary-700">
                  {effectiveCount} {effectiveCount === 1 ? 'site' : 'sites'} selected
                </span>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-primary-600 underline underline-offset-2 cursor-pointer"
                >
                  Clear
                </button>
                {showSelectAllBanner && (
                  <>
                    <div className="w-px h-5 bg-primary-200 mx-1" />
                    <button
                      type="button"
                      onClick={handleSelectAllInList}
                      className="text-sm text-primary-600 underline underline-offset-2 cursor-pointer font-semibold"
                    >
                      Select all {total} sites
                    </button>
                  </>
                )}
                <div className="w-px h-5 bg-primary-200 mx-1" />
                <Button
                  size="sm"
                  color="primary"
                  className="font-semibold"
                  startContent={<Icon as={SparklesIcon} size="sm" color="inherit" />}
                  onPress={() => setIsMassUpdateOpen(true)}
                >
                  Mass Update
                </Button>
              </div>
            )}

            <CardBody className="p-0">
              {visibleSites.length > 0 ? (
                <>
                  <SitesTable
                    sites={visibleSites}
                    selectedKeys={heroSelectedKeys}
                    onSelectionChange={handleSelectionChange}
                    onRowAction={(key) => navigate({ to: '/sites/$siteId', params: { siteId: String(key) } })}
                  />
                  <TablePagination
                    page={page}
                    pageSize={PAGE_SIZE}
                    total={total}
                    visibleCount={visibleSites.length}
                    isFetching={isFetching}
                    onChange={setPage}
                  />
                </>
              ) : (
                <EmptyState
                  icon={PlusIcon}
                  title={searchInput ? 'No sites found matching your search' : 'No sites added yet'}
                  description="Get started by adding your first site"
                  variant={searchInput ? 'search' : 'default'}
                  action={
                    searchInput
                      ? undefined
                      : {
                          label: 'Add Site',
                          icon: PlusIcon,
                          onPress: () => {},
                          color: 'primary',
                        }
                  }
                />
              )}
            </CardBody>
          </Card>
        )}
      </div>

      <ImportSiteFromCrmModal isOpen={isImportOpen} onClose={onImportClose} />
      {isMassUpdateOpen && (
        <MassUpdateModal
          isOpen
          onClose={() => setIsMassUpdateOpen(false)}
          scope={
            selection.mode === 'cohort'
              ? { type: 'cohort', exclude: [...selection.excluded] }
              : { type: 'ids', siteIds: [...selection.selected] }
          }
          effectiveCount={effectiveCount}
          onApplied={handleApplied}
        />
      )}
    </div>
  );
}
